import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { auth } from '../firebase';

interface Props {
  title: string;
  description: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

function reauthErrorMessage(code: string | undefined, isGoogleUser: boolean): string {
  if (isGoogleUser) {
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      return 'Google sign-in was cancelled';
    }
    if (code === 'auth/network-request-failed') {
      return 'Network error — check your connection';
    }
    return code ? `Google re-authentication failed (${code})` : 'Google re-authentication failed';
  }
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Incorrect password';
    case 'auth/too-many-requests':
      return 'Too many attempts — try again in a few minutes';
    case 'auth/requires-recent-login':
    case 'auth/user-token-expired':
      return 'Session expired — please sign out and sign in again';
    case 'auth/network-request-failed':
      return 'Network error — check your connection';
    case 'auth/user-mismatch':
      return 'Account mismatch — please sign out and sign in again';
    case 'auth/user-disabled':
      return 'This account is disabled';
    default:
      return code ? `Re-authentication failed (${code})` : 'Re-authentication failed';
  }
}

function maskPhone(phone: string | null | undefined): string {
  if (!phone) return 'your enrolled number';
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 4) return '****';
  return '+' + '*'.repeat(Math.max(digits.length - 4, 3)) + digits.slice(-4);
}

export function DeleteConfirmModal({ title, description, onConfirm, onCancel }: Props) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  // MFA state — populated when reauth returns auth/multi-factor-auth-required.
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string>('');
  const [mfaPhoneLabel, setMfaPhoneLabel] = useState<string>('');
  const [mfaCode, setMfaCode] = useState('');

  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Firebase leaks the reCAPTCHA widget if not cleared on unmount.
  useEffect(() => () => {
    try { recaptchaVerifierRef.current?.clear(); } catch { /* widget may already be gone */ }
    recaptchaVerifierRef.current = null;
  }, []);

  const isGoogleUser = useMemo(() => {
    const user = auth.currentUser;
    if (!user) return false;
    return user.providerData.some(p => p.providerId === 'google.com');
  }, []);

  const ensureRecaptcha = useCallback((): RecaptchaVerifier => {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    if (!recaptchaContainerRef.current) {
      throw new Error('reCAPTCHA container not mounted');
    }
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    return verifier;
  }, []);

  const handleEmailReauth = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw Object.assign(new Error('Not authenticated'), { code: 'auth/no-current-user' });
    }
    // Trim: password managers occasionally paste a trailing space or NBSP.
    const credential = EmailAuthProvider.credential(user.email, password.trim());
    await reauthenticateWithCredential(user, credential);
  }, [password]);

  const handleGoogleReauth = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw Object.assign(new Error('Not authenticated'), { code: 'auth/no-current-user' });
    }
    const provider = new GoogleAuthProvider();
    await reauthenticateWithPopup(user, provider);
  }, []);

  const startMfaPhoneFlow = useCallback(async (resolver: MultiFactorResolver) => {
    const phoneHint = resolver.hints.find(
      (h): h is PhoneMultiFactorInfo => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );
    if (!phoneHint) {
      throw new Error('MFA required but no phone factor enrolled on this account');
    }
    const verifier = ensureRecaptcha();
    const phoneProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneProvider.verifyPhoneNumber(
      { multiFactorHint: phoneHint, session: resolver.session },
      verifier,
    );
    setMfaResolver(resolver);
    setMfaVerificationId(verificationId);
    setMfaPhoneLabel(maskPhone(phoneHint.phoneNumber));
  }, [ensureRecaptcha]);

  const handleSubmit = useCallback(async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setProcessing(true);

    try {
      if (isGoogleUser) {
        await handleGoogleReauth();
      } else {
        await handleEmailReauth();
      }
    } catch (err: unknown) {
      const code = (err as { code?: string } | null)?.code;
      const message = (err as { message?: string } | null)?.message;
      console.error('[DeleteConfirmModal] reauth failed', { code, message });

      // MFA-enrolled account: Firebase returns a resolver, not a failure.
      // Kick off the SMS OTP step and swap the modal UI to code entry.
      if (code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, err as MultiFactorError);
          await startMfaPhoneFlow(resolver);
          setProcessing(false);
          return;
        } catch (mfaErr: unknown) {
          const mfaErrCode = (mfaErr as { code?: string } | null)?.code;
          const mfaErrMsg = (mfaErr as { message?: string } | null)?.message;
          console.error('[DeleteConfirmModal] MFA start failed', { code: mfaErrCode, message: mfaErrMsg });
          setError(mfaErrCode ? `Could not send verification code (${mfaErrCode})` : 'Could not send verification code');
          setProcessing(false);
          return;
        }
      }

      setError(reauthErrorMessage(code, isGoogleUser));
      setProcessing(false);
      return;
    }

    try {
      await onConfirm();
    } catch {
      setError('Action failed');
    } finally {
      setProcessing(false);
    }
  }, [isGoogleUser, handleEmailReauth, handleGoogleReauth, onConfirm, startMfaPhoneFlow]);

  const handleMfaVerify = useCallback(async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!mfaResolver || !mfaVerificationId) return;
    setError('');
    setProcessing(true);

    try {
      const cred = PhoneAuthProvider.credential(mfaVerificationId, mfaCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await mfaResolver.resolveSignIn(assertion);
    } catch (err: unknown) {
      const code = (err as { code?: string } | null)?.code;
      const message = (err as { message?: string } | null)?.message;
      console.error('[DeleteConfirmModal] MFA verify failed', { code, message });
      const ui = code === 'auth/invalid-verification-code'
        ? 'Incorrect verification code'
        : code === 'auth/code-expired'
          ? 'Verification code expired — cancel and try again'
          : code === 'auth/too-many-requests'
            ? 'Too many attempts — try again in a few minutes'
            : code
              ? `Verification failed (${code})`
              : 'Verification failed';
      setError(ui);
      setProcessing(false);
      return;
    }

    try {
      await onConfirm();
    } catch {
      setError('Action failed');
    } finally {
      setProcessing(false);
    }
  }, [mfaResolver, mfaVerificationId, mfaCode, onConfirm]);

  const showMfa = mfaResolver !== null && mfaVerificationId !== '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-600 mb-4">{description}</p>

        {error && (
          <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {showMfa ? (
          <form onSubmit={handleMfaVerify}>
            <p className="text-sm text-gray-600 mb-1">
              Two-factor required. We sent a 6-digit code to {mfaPhoneLabel}.
            </p>
            <p className="text-xs text-gray-500 mb-4">Enter it below to confirm this action.</p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4 tracking-widest text-center"
              autoFocus
              autoComplete="one-time-code"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mfaCode.length !== 6 || processing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processing ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</> : 'Verify & Confirm'}
              </button>
            </div>
          </form>
        ) : isGoogleUser ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">Re-authenticate with Google to confirm.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processing ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming...</> : 'Re-authenticate with Google'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-gray-600 mb-4">Enter your password to confirm.</p>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              autoFocus
              autoComplete="current-password"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!password || processing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processing ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming...</> : 'Confirm'}
              </button>
            </div>
          </form>
        )}

        {/* reCAPTCHA anchor — invisible, required for SMS MFA verification. */}
        <div ref={recaptchaContainerRef} />
      </div>
    </div>
  );
}
